// src/components/ClientForm/ClientForm.jsx
import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { listUsersByTeamOne } from '../graphql/queries';
import { CREATE_CLIENTFIX2, UPDATE_CLIENTFIX2 } from '../graphql/mutations';
import { v4 as uuidv4 } from 'uuid';
import { AddressAutofill } from '@mapbox/search-js-react';
import './ClientForm.css';

const ClientForm = ({
  isEditing = false,
  selectedClient = null,
  onClientAdded,
  onClose,
  userGroups,
  handleDeleteClient,
  refetch
}) => {
  // Initialize state based on whether we're editing or adding a new client
  const [clientData, setClientData] = useState(selectedClient || {
    id: uuidv4(),
    name: '',
    last_name: '',
    email: '',
    address: '',
    city: '',
    county: '',
    zip_code: '',
    prefered_lang: '',
    insurance_picked: '',
    last_contacted_date: '',
    navigator: '',
    payment_per_month: '',
    phone: '',
    plan: '',
    userId: '',
    callStatus: '',
  });

  const [phoneError, setPhoneError] = useState('');
  const [noAddress, setNoaddress] = useState(false);

  // Query to get navigators
  const { loading: usersLoading, error: usersError, data: usersData } = useQuery(listUsersByTeamOne);

  // Build navigator options with user.id as the key, and "FirstName LastName" as the displayed name
  const navigatorOptions = usersData?.listUserFixes?.items?.map(user => ({
    id: user.id,
    name: `${user.first_name.charAt(0).toUpperCase() + user.first_name.slice(1).toLowerCase()} ${user.last_name.charAt(0).toUpperCase() + user.last_name.slice(1).toLowerCase()}`
  })) || [];

  // Mutations for add and update
  const [addClient] = useMutation(CREATE_CLIENTFIX2, {
    onCompleted: () => {
      onClientAdded();
      refetch(); // trigger refetch after the mutation completes successfully
      onClose();
    },
    onError: (error) => console.error("Error adding client:", error)
  });

  const [updateClient] = useMutation(UPDATE_CLIENTFIX2, {
    onCompleted: () => {
      onClientAdded();
      refetch(); // trigger refetch after the mutation completes successfully
      onClose();
    },
    onError: (error) => console.error("Error updating client:", error)
  });

  // Handle input changes for general fields
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    let formattedValue = value;

    if (['name', 'last_name', 'email'].includes(name)) {
      formattedValue = value.toLowerCase();
    }

    if (name === 'address address-search') {
      setClientData({ ...clientData, ['address']: formattedValue });
    }

    if (name === 'phone') {
      formattedValue = value.replace(/[^\d]/g, ''); // Remove non-numeric characters
      if (formattedValue.length > 10) {
        setPhoneError('Phone number cannot be longer than 10 digits.');
      } else {
        setPhoneError('');
      }
    }

    // if (name === 'zip_code') {
    //   formattedValue = value.replace(/[^\d]/g, ''); // Allow only numeric input
    //   if (formattedValue.length > 5) {
    //     formattedValue = formattedValue.slice(0, 5); // Limit to 5 digits
    //   }
    // }

    if (name === 'prefered_lang') {
      formattedValue = value.split(',').map(lang => lang.trim());
    }

    setClientData(prevState => ({
      ...prevState,
      [name]: formattedValue
    }));
  };

  // Handle form submission
  const handleSubmit = () => {
    if (clientData.phone.length !== 10) {
      setPhoneError('Phone number must be exactly 10 digits.');
      return;
    }
    if (Number.isInteger(clientData.payment_per_month) && clientData.payment_per_month >= 0) {
      // Additional payment_per_month logic if needed
    } else {
      clientData.payment_per_month = 0;
    }

    // Allowed fields based on UpdateClientFix2Input
    const allowedFields = [
      'id',
      'name',
      'last_name',
      'email',
      'address',
      'city',
      'county',
      'zip_code',
      'prefered_lang',
      'insurance_picked',
      'last_contacted_date',
      'navigator',
      'payment_per_month',
      'phone',
      'plan',
      'userId',
      'callStatus',
    ];

    // Filter clientData to include only allowed fields
    const filteredClientData = Object.keys(clientData)
      .filter((key) => allowedFields.includes(key))
      .reduce((obj, key) => {
        obj[key] = typeof clientData[key] === 'string' && key !== 'callStatus'
          ? clientData[key].toLowerCase()
          : clientData[key];
        return obj;
      }, {});

    // Perform the mutation
    if (isEditing) {
      updateClient({
        variables: { input: filteredClientData }
      });
    } else {
      addClient({ variables: { input: filteredClientData } });
    }
  };

  return (
    <div>
      <button className="add_usr_close-btn" onClick={onClose}>
        X
      </button>
      <h2>{isEditing ? "Edit Client" : "Add New Client"}</h2>
      <div className="client-form-grid">
        <div className="form-group">
          <label htmlFor="name">First Name</label>
          <input
            id="name"
            name="name"
            placeholder="First Name"
            value={clientData.name}
            onChange={handleInputChange}
          />
        </div>
        <div className="form-group">
          <label htmlFor="last_name">Last Name</label>
          <input
            id="last_name"
            name="last_name"
            placeholder="Last Name"
            value={clientData.last_name}
            onChange={handleInputChange}
          />
        </div>
        <div className="form-group full-width">
          <label htmlFor="email">Email</label>
          <input
            id="email"
            name="email"
            placeholder="Email"
            value={clientData.email}
            onChange={handleInputChange}
          />
        </div>
        <div className="form-group">
          <label htmlFor="phone">Phone Number</label>
          <input
            id="phone"
            name="phone"
            placeholder="Phone Number"
            value={clientData.phone}
            onChange={handleInputChange}
          />
          {phoneError && <p className="error-text">{phoneError}</p>}
        </div>
        <div className="form-group">
          <label htmlFor="prefered_lang">Preferred Language</label>
          <select
            id="prefered_lang"
            name="prefered_lang"
            value={clientData.prefered_lang}
            onChange={handleInputChange}
          >
            <option value="" disabled hidden>Select Language</option>
            <option value="english">English</option>
            <option value="spanish">Spanish</option>
            <option value="french">French</option>
            <option value="german">German</option>
            <option value="chinese">Chinese</option>
            <option value="japanese">Japanese</option>
            <option value="arabic">Arabic</option>
            <option value="pashto">Pashto</option>
            <option value="turkish">Turkish</option>
            <option value="nigerian">Nigerian</option>
            <option value="dari">Dari</option>
            <option value="Other">Other</option>
          </select>
        </div>
        {/* Add other form fields similarly */}
        <div className="form-group">
          <div className='labelHolder'>
            <label className="required-asterisk">Address:</label>
            <label style={{ display: "flex", alignItems: "center", cursor: "pointer" }}>
              <input
                type="checkbox"
                checked={noAddress || false}
                onChange={(e) => {
                  const isChecked = e.target.checked;
                  setNoaddress(isChecked);

                  if (isChecked) {
                    setClientData(prevState => ({
                      ...prevState,
                      address: "N/A",
                      city: "N/A",
                      county: "N/A",
                      zip_code: "N/A"
                    }));
                  } else {
                    setClientData(prevState => ({
                      ...prevState,
                      address: "",
                      city: "",
                      county: "",
                      zip_code: ""
                    }));
                  }
                }}
              />
              <span style={{ marginLeft: "5px" }}>No Address</span>
            </label>
          </div>

          {/* Mapbox Autocomplete */}
          <AddressAutofill
            accessToken="pk.eyJ1IjoiZXVsaXNlcy1mcmFuY28iLCJhIjoiY203YzUyZHV4MDBxejJtb3N5dGI5dnF1cCJ9.9Zzj5B9hOEcQyw5AGHOrCA"
            onRetrieve={(res) => {
              const context = res.features[0].properties;
              setClientData(prevState => ({
                ...prevState,
                address: context.address_line1 || prevState.address,
                city: context.address_level2 || prevState.city,
                county: context.district || prevState.county,
                zip_code: context.postcode || prevState.zip_code
              }));
            }}
          >
            <input
              id="address"
              name="address"
              placeholder="Address"
              type="text"
              autoComplete="address-line1"
              style={{ width: "100%", resize: "vertical" }}
              value={clientData.address}
              onChange={handleInputChange}
            />
          </AddressAutofill>
        </div>

        {/* Editable City Input */}
        <div className="form-group">
          <label htmlFor="city">City</label>
          <input
            id="city"
            name="city"
            placeholder="City"
            type="text"
            autoComplete="address-level2"
            value={clientData.city}
            onChange={handleInputChange}
            required
          />
        </div>

        {/* Editable County Input */}
        <div className="form-group">
          <label htmlFor="county">County</label>
          <input
            id="county"
            name="county"
            placeholder="County"
            type="text"
            value={clientData.county}
            onChange={handleInputChange}
            required
          />
        </div>

        {/* Editable Zip Code Input */}
        <div className="form-group">
          <label htmlFor="zip_code">Zip Code</label>
          <input
            id="zip_code"
            name="zip_code"
            placeholder="Zip Code"
            type="text"
            autoComplete="postal-code"
            value={clientData.zip_code}
            onChange={handleInputChange}
            required
          />
        </div>
        <div className="form-group">
          <label htmlFor="last_contacted_date">Last Contacted Date</label>
          <input
            type="date"
            id="last_contacted_date"
            name="last_contacted_date"
            value={clientData.last_contacted_date}
            onChange={handleInputChange}
          />
        </div>

        {/* Navigator Selection */}
        <div className="form-group">
          <label htmlFor="navigator">Navigator</label>
          <select
            id="navigator"
            name="navigator"
            value={clientData.userId || ""}  // Make sure the select uses clientData.userId
            onChange={(e) => {
              const selectedUserId = e.target.value;
              const selectedNavigator = navigatorOptions.find(option => option.id === selectedUserId);

              setClientData(prevState => ({
                ...prevState,
                userId: selectedUserId,               // Store the navigator's ID
                navigator: selectedNavigator?.name || "" // Optionally keep the name, if needed
              }));
            }}
          >
            <option value="">Select Navigator...</option>
            {navigatorOptions
              .sort((a, b) => a.name.localeCompare(b.name))
              .map(option => (
                <option key={option.id} value={option.id}>
                  {option.name}
                </option>
              ))}
          </select>
        </div>
        <div className="form-group">
          <label htmlFor="payment_per_month">Payment Per Month</label>
          <input
            id="payment_per_month"
            name="payment_per_month"
            placeholder="Payment Per Month"
            value={clientData.payment_per_month}
            onChange={handleInputChange}
          />
        </div>
        <div className="form-group">
          <label htmlFor="callStatus">Client Status</label>
          <select
            id="callStatus"
            name="callStatus"
            value={clientData.callStatus}
            onChange={handleInputChange}
          >
            <option value="">Select Status...</option>
            <option value="Opt Out">Opt Out</option>
            <option value="Completed">Completed</option>
            <option value="Unreachable">Unreachable</option>
          </select>
        </div>
      </div>
      <button className='overlay-add-client' onClick={handleSubmit}>
        {isEditing ? "Update Client" : "Add Client"}
      </button>
      {userGroups?.includes('Admin') && isEditing && (
        <button className='overlay-delete-client' onClick={handleDeleteClient}>
          Delete Client
        </button>
      )}
    </div>
  );
};

export default ClientForm;
